'use server';

import logApiError from 'utils/log/logApiError';
import { pianoIdApi, pianoIdEndpoints } from '@/app_actions/Checkout/utils';
import { pianoErrorCodes } from '@/app_lib/piano/pianoErrorCodes';

interface GoogleUserPayload {
  email: string;
  givenName: string;
  familyName: string;
}

interface PianoUser {
  uid: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

// Piano API response interfaces (keeping snake_case as that's what Piano API returns)
interface PianoApiUserResponse {
  uid: string;
  email: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  first_name?: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  last_name?: string;
}

interface PianoApiTokenResponse {
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  access_token: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  token_type: string;
  // biome-ignore lint/style/useNamingConvention: Piano API uses snake_case
  expires_in: number;
}

interface PianoAuthResponse {
  success: boolean;
  pianoUid?: string;
  pianoToken?: string;
  isNewUser?: boolean;
  message?: string;
}

interface PianoAuthError {
  success: false;
  message: string;
}

type PianoAuthResult = PianoAuthResponse | PianoAuthError;

async function checkUserExists(email: string): Promise<PianoUser | null> {
  try {
    const requestUrl = `${pianoIdApi(pianoIdEndpoints.getUser)}&email=${encodeURIComponent(email)}`;
    const userCheckResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (userCheckResponse.ok) {
      const userResponse = (await userCheckResponse.json()) as PianoUser;
      return userResponse || null;
    }
    return null;
  } catch (error) {
    logApiError(error as Error, '[checkUserExists]');
    return null;
  }
}

async function registerGoogleUser(email: string, givenName: string, familyName: string): Promise<PianoUser> {
  try {
    const requestUrl = `${pianoIdApi(pianoIdEndpoints.register)}&email=${encodeURIComponent(email)}&first_name=${encodeURIComponent(givenName || '')}&last_name=${encodeURIComponent(familyName || '')}&is_passwordless=true&password=`;

    const registerResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!registerResponse.ok) {
      const errorData = await registerResponse.json();

      // Check for user already exists error
      if (errorData.error_code_list && errorData.error_code_list[0].code === pianoErrorCodes.userExistsError) {
        throw new Error('A user with this email already exists.');
      }

      throw new Error(`Registration failed: ${errorData.error_code_list?.[0]?.message || 'Unknown error'}`);
    }

    const registerResult = (await registerResponse.json()) as PianoApiUserResponse;
    if (!registerResult) {
      throw new Error('Registration succeeded but no user data returned');
    }

    // Map Piano API response to our interface
    return {
      email: registerResult.email,
      firstName: registerResult.first_name,
      lastName: registerResult.last_name,
      uid: registerResult.uid,
    };
  } catch (error) {
    logApiError(error as Error, '[registerGoogleUser]');
    throw error;
  }
}

async function generateUserToken(uid: string): Promise<string> {
  try {
    // Use the Piano ID API to generate a token for the user
    const requestUrl = `${pianoIdApi('/publisher/token')}&uid=${uid}`;

    const tokenResponse = await fetch(requestUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(`Token generation failed: ${errorData.error_code_list?.[0]?.message || 'Unknown error'}`);
    }

    const tokenResult = (await tokenResponse.json()) as PianoApiTokenResponse;
    return tokenResult.access_token;
  } catch (error) {
    logApiError(error as Error, '[generateUserToken]');
    throw error;
  }
}

/**
 * Validates the payload for Piano authentication via Google user data
 */
function validateGoogleUserPayload(payload: GoogleUserPayload): { isValid: boolean; errorMessage?: string } {
  const { email } = payload;

  if (!email || typeof email !== 'string') {
    return { errorMessage: 'Valid email is required', isValid: false };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { errorMessage: 'Invalid email format', isValid: false };
  }

  return { isValid: true };
}

/**
 * Server action to authenticate a user in Piano using Google One Tap user data
 * Follows the same patterns as other authentication actions in accountActions.ts
 */
async function pianoAuthWithGoogleAction(payload: GoogleUserPayload): Promise<PianoAuthResult> {
  try {
    const { email, givenName, familyName } = payload;

    // Validate payload
    const validation = validateGoogleUserPayload(payload);
    if (!validation.isValid) {
      return {
        message: validation.errorMessage || 'Invalid payload',
        success: false,
      };
    }

    // Check if user exists in Piano
    let pianoUser = await checkUserExists(email);
    let isNewUser = false;

    // Register user in Piano if they don't exist
    if (!pianoUser) {
      try {
        pianoUser = await registerGoogleUser(email, givenName || '', familyName || '');
        isNewUser = true;
      } catch (registrationError) {
        return {
          message: registrationError instanceof Error ? registrationError.message : 'Registration failed',
          success: false,
        };
      }
    }

    // Generate Piano user token
    let pianoToken: string;
    try {
      pianoToken = await generateUserToken(pianoUser.uid);
    } catch (tokenError) {
      return {
        message: tokenError instanceof Error ? tokenError.message : 'Token generation failed',
        success: false,
      };
    }

    return {
      isNewUser,
      message: isNewUser
        ? 'User registered successfully in Piano via Google'
        : 'User authenticated successfully in Piano via Google',
      pianoToken,
      pianoUid: pianoUser.uid,
      success: true,
    };
  } catch (error) {
    logApiError(error as Error, '[pianoAuthWithGoogleAction]');
    return {
      message: 'An unexpected error occurred during Piano authentication',
      success: false,
    };
  }
}

export { pianoAuthWithGoogleAction };
export type { PianoAuthResult };
